import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  Font,
} from '@react-pdf/renderer';
import moment from 'moment';

Font.register({
  family: 'Mona-Sans',
  fonts: [
    {src: '/MonaSans-Regular.ttf'},
    {src: '/MonaSans-Medium.ttf', fontWeight: 'medium'},
    {src: '/MonaSans-Bold.ttf', fontWeight: 'bold'},
    {src: '/MonaSans-SemiBold.ttf', fontWeight: 'semibold'},
  ],
});

const styles = StyleSheet.create({
  page: {
    paddingVertical: 25,
    paddingHorizontal: 30,
    fontFamily: 'Mona-Sans',
    fontSize: 10,
    backgroundColor: '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  logo: {
    width: 121,
    height: 27,
  },
  headerRef: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  labelIdGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  headerRefName: {
    color: '#161616',
    fontSize: 10,
    fontWeight: 'medium',
    textTransform: 'uppercase',
  },
  headerGen: {
    color: '#000000',
    fontWeight: 'semibold',
    fontSize: 10,
  },
  headerRefID: {
    color: '#000000',
    fontSize: 12,
    fontWeight: 'medium',
  },
  timestamp: {
    fontSize: 10,
    color: '#161616',
    fontWeight: 'medium',
    textTransform: 'uppercase',
  },
  section: {
    marginTop: 16,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 'medium',
    color: '#0B1C56',
    backgroundColor: '#EEF3FC',
    borderRadius: 4,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: 20,
    paddingRight: 12,
    paddingTop: 10,
    paddingBottom: 10,
    marginBottom: 4,
  },
  sectionContent: {
    paddingLeft: 20,
    paddingRight: 12,
    paddingTop: 15,
    paddingBottom: 15,
    border: '1px solid #EEF3FC',
    borderRadius: 4,
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 24,
  },
  twoColumnContainer: {
    flexDirection: 'row',
    width: '100%',
    gap: 20,
  },
  column: {
    flex: 1,
    flexDirection: 'column',
  },
  gridContainer: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: 12,
  },
  dataPair: {
    flexGrow: 1,
    flexShrink: 0,
    paddingRight: 15,
    marginBottom: 10,
  },
  dataKey: {
    fontSize: 10,
    color: '#161616',
    textTransform: 'uppercase',
    fontWeight: 'semibold',
    marginBottom: 5,
  },
  dataValue: {
    fontSize: 12,
    fontWeight: 'medium',
    color: '#4E4E4E',
    lineHeight: 1.4,
  },
  subHeading: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#000000',
    textTransform: 'uppercase',
    marginBottom: 10,
    marginTop: 5,
  },
  footer: {
    flexDirection: 'column',
    marginTop: 30,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#E4E4E6',
    alignItems: 'center',
  },
  footerTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: 8,
  },
  footerText: {
    fontSize: 9,
    color: '#858DAA',
    fontWeight: 'bold',
    textAlign: 'center',
    width: '33%',
  },
});

const DataPair = ({label, value, style = {}}) => (
  <View style={[styles.dataPair, style]}>
    <Text style={styles.dataKey}>{label}</Text>
    <Text style={styles.dataValue}>{value || '-'}</Text>
  </View>
);

export const ProfileReport = ({
  data,
  logoImage,
  userName,
  quickSummary,
  reportingEntityName,
}) => {
  const user = data?.userEntity || {};
  const kyc = data?.employerEntity || {};
  const financial = data?.financialData || {};
  const behavioral = data?.behavioralData || {};
  const location = data?.locationData || {};
  const network = data?.networkData || {};

  const {riskScoreValue, totalCases, totalEvents, totalOpenCases, riskTier} =
    quickSummary || {};

  const currentDate = moment().format('DD/MM/YYYY, hh:mmA').toUpperCase();
  const displayDate = currentDate;

  const profileCreatedDate = user?.createdAt
    ? moment(user.createdAt).format('Do MMMM YYYY')
    : '-';

  const profileUpdatedDate = user?.updatedAt
    ? moment(user.updatedAt).format('Do MMMM YYYY')
    : '-';

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header} wrap={false}>
          {logoImage && <Image style={styles.logo} src={logoImage} />}
        </View>

        <View style={styles.headerRef} wrap={false}>
          <View style={styles.labelIdGroup}>
            <Text style={styles.headerRefName}>Report Title</Text>
            <Text style={styles.headerRefID}>
              {user?.user_name} Profile Report
            </Text>
          </View>
          <Text style={styles.headerGen}>REPORT GENERATED ON</Text>
        </View>

        <View style={styles.headerRef} wrap={false}>
          <View style={styles.labelIdGroup}>
            <Text style={styles.headerRefName}>PROFILE ID</Text>
            <Text style={styles.headerRefID}>{user?.user_id || '-'}</Text>
          </View>
          <Text style={styles.timestamp}>{displayDate}</Text>
        </View>

        {/* RISK SUMMARY */}
        <View style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>Risk Summary</Text>
          <View style={styles.sectionContent}>
            <View style={styles.gridContainer}>
              <DataPair
                label="CURRENT RISK LEVEL"
                value={riskTier}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="CURRENT RISK SCORE"
                value={riskScoreValue}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="OPEN CASES"
                value={totalOpenCases}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="TOTAL CASES"
                value={totalCases}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="PROFILE CREATED"
                value={profileCreatedDate}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="PROFILE LAST UPDATED"
                value={profileUpdatedDate}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="TOTAL EVENTS"
                value={totalEvents}
                style={{width: '100%', flexGrow: 0}}
              />
            </View>
          </View>
        </View>

        {/* CUSTOMER INFO */}
        <View style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>
            Customer Information & Verification
          </Text>
          <View style={styles.sectionContent}>
            <View style={styles.gridContainer}>
              <DataPair
                label="FULL NAME"
                value={user?.user_name}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="EMAIL"
                value={user?.user_email}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="PHONE NUMBER"
                value={user?.user_mobile}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="NATIONALITY"
                value={kyc?.nationality}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="ACCOUNT STATUS"
                value={user?.account_status}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="KYC COVERAGE"
                value={kyc?.verification_status}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="IDENTITY CONFIDENCE"
                value={kyc?.confidence_score}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="PROFILE STABILITY - NAME"
                value={user?.name_changes_count}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="PROFILE STABILITY - PHONE"
                value={user?.phone_changes_count}
                style={{width: '33%', flexGrow: 0}}
              />
            </View>
          </View>
        </View>

        {/* FINANCIAL */}
        {/* <View style={styles.section} wrap={true}>
          <Text style={styles.sectionTitle}>Financial Activity Summary</Text>
          <View style={styles.sectionContent}>
            <View style={styles.gridContainer}>
              <DataPair
                label="LIFETIME TX COUNT"
                value={financial?.lifetimeCount}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="LIFETIME TX VALUE"
                value={financial?.lifetimeValue}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="30-DAY COUNT"
                value={financial?.rollingCount}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="30-DAY VALUE"
                value={financial?.rollingValue}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="PRIMARY CHANNEL"
                value={financial?.primaryChannel}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="TOP COUNTERPARTIES"
                value={financial?.topCounterparties}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="LOAN SUMMARY"
                value={financial?.loanSummary}
                style={{width: '66%', flexGrow: 0}}
              />
            </View>
          </View>
        </View> */}

        {/* RISK INDICATORS */}
        {/* <View style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>Key Risk Indicators & Flags</Text>
          <View style={styles.sectionContent}>
            <View style={styles.twoColumnContainer}>
              <View style={styles.column}>
                <Text style={styles.subHeading}>BEHAVIORAL & DEVICE</Text>
                <DataPair
                  label="Active Devices"
                  value={behavioral?.activeDevices}
                  style={{marginBottom: 8}}
                />
                <DataPair
                  label="Primary Device ID"
                  value={behavioral?.primaryDeviceId}
                  style={{marginBottom: 8}}
                />
                <DataPair
                  label="Login Frequency"
                  value={behavioral?.loginFrequency}
                  style={{marginBottom: 8}}
                />

                <Text style={styles.subHeading}>
                  LOCATION & GEOGRAPHIC ACTIVITY
                </Text>
                <DataPair
                  label="Address Verification"
                  value={location?.addressStatus}
                  style={{marginBottom: 8}}
                />
                <DataPair
                  label="Recent Geo Changes"
                  value={location?.geoChanges}
                  style={{marginBottom: 8}}
                />
              </View>

              <View style={styles.column}>
                <Text style={styles.subHeading}>NETWORK & RELATIONSHIP</Text>
                <DataPair
                  label="Active Loans"
                  value={network?.activeLoans}
                  style={{marginBottom: 8}}
                />
                <DataPair
                  label="Defaulted Loans"
                  value={network?.defaultedLoans}
                  style={{marginBottom: 8}}
                />
              </View>
            </View>
          </View>
        </View> */}

        {/* REPORTING ENTITY */}
        <View style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>Reporting Entity</Text>
          <View style={styles.sectionContent}>
            <View style={styles.gridContainer}>
              <DataPair
                label="REPORTING ENTITY NAME"
                value={reportingEntityName}
                style={{width: '100%', flexGrow: 0}}
              />
            </View>
          </View>
        </View>

        <View style={styles.footer} wrap={false}>
          <View style={styles.footerTopRow}>
            <Text style={styles.footerText}>
              GENERATED BY: {(userName || 'SYSTEM').toUpperCase()}
            </Text>
            <Text style={styles.footerText}>GENERATED: {currentDate}</Text>
            <Text style={styles.footerText}>DOWNLOADED: {currentDate}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};
